import React from 'react';
import FilingPreferencesSection from './FilingPreferencesSection';

/**
 * One switch row, as in the app's Settings modal: a strong label and a small
 * hint on the left, the switch on the right. The whole row is the label.
 */
function SettingsToggleRow({ id, label, hint, checked, disabled, onChange }) {
  return (
    <label className={`fl-settings-row${disabled ? ' is-disabled' : ''}`} htmlFor={id}>
      <span className="fl-settings-row-text">
        <strong id={`${id}-label`}>{label}</strong>
        <small id={`${id}-hint`}>{hint}</small>
      </span>
      <input
        id={id}
        type="checkbox"
        role="switch"
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-hint`}
        checked={checked}
        disabled={disabled}
        onChange={(e) => { if (!disabled) onChange(e.target.checked); }}
      />
    </label>
  );
}

/**
 * Library settings (the header cog): how this library is filed, then (for
 * company members) the user-level Company mode switch, then its filing
 * preferences.
 * Parts the backend or the parent can't offer are disabled or left out.
 */
export default function LibrarySettingsView({
  isCompany,
  companyName,
  viewingUserLabel,
  stats,
  autoSortBusy,
  onSetAutoSort,
  filingPreferences,
  companyMode,
}) {
  let subtitle = 'Your personal library';
  if (isCompany) subtitle = `${companyName} library · shared with ${viewingUserLabel ? 'the team' : 'your team'}`;
  else if (viewingUserLabel) subtitle = `${viewingUserLabel}'s personal library`;

  const autoSort = stats?.auto_sort !== false;
  const autoSortEditable = stats?.auto_sort_editable === true;

  return (
    <section className="fl-settings" aria-labelledby="fl-settings-title">
      <div className="fl-list-head">
        <div className="fl-list-heading">
          <h3 id="fl-settings-title" className="fl-view-title">Library settings</h3>
          <p className="fl-view-subtitle">{subtitle}</p>
        </div>
      </div>

      <section className="fl-settings-card" aria-labelledby="fl-settings-filing-title">
        <div className="fl-settings-card-head">
          <h4 id="fl-settings-filing-title" className="fl-settings-card-title">Filing</h4>
        </div>
        <SettingsToggleRow
          id="fl-settings-autosort"
          label="Auto-sort new files"
          hint="Atlas files new uploads and the files it makes into folders. When off, they wait in Unfiled with a suggested folder."
          checked={autoSort}
          disabled={autoSortBusy || !autoSortEditable}
          onChange={onSetAutoSort}
        />
        {stats && !autoSortEditable ? (
          <p className="fl-settings-card-foot">Auto-sort can&apos;t be changed for this library.</p>
        ) : null}
      </section>

      {companyMode ? (
        <section className="fl-settings-card" aria-labelledby="fl-settings-company-title">
          <div className="fl-settings-card-head">
            <h4 id="fl-settings-company-title" className="fl-settings-card-title">Company mode</h4>
          </div>
          <SettingsToggleRow
            id="fl-settings-personal-memories"
            label="Use personal memories in company mode"
            hint="Allow personal memories to help while working in the shared workspace."
            checked={Boolean(companyMode.preferences.use_personal_memories_in_company_mode)}
            disabled={Boolean(companyMode.saving) || !companyMode.preferences.persistence_available}
            onChange={(checked) => companyMode.onUpdate({ use_personal_memories_in_company_mode: checked })}
          />
          <p className="fl-settings-card-foot">Also in Settings → Atlas Settings.</p>
        </section>
      ) : null}

      <FilingPreferencesSection
        {...filingPreferences}
        isCompany={isCompany}
        companyName={companyName}
        viewingUserLabel={viewingUserLabel}
        autoSortOff={stats?.auto_sort === false}
      />

    </section>
  );
}
